import { FC, PropsWithChildren, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { UserState } from '../user/UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { RecordingState } from '../recording/RecordingState';
import { IntroScreenState } from './IntroScreenState';
import { useFadeTransition } from '../hooks/useFadeTransition';
import { IntroRecordButton } from './IntroRecordButton';
import { VideoBackground } from './VideoBackground';

const TRANSITION_MS = 900;

const useStyles = createUseStyles({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50000, // must be higher than ionic z-index on recording sheet modal
        display: 'flex',
        flexDirection: 'column'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    },
    hero: {
        position: 'relative',
        height: '65dvh',
        minHeight: 350,
        width: '100%',
        overflow: 'hidden',
        flexShrink: 0
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        background: `linear-gradient(180deg,
            rgba(0,0,0,0.05) 0%,
            rgba(0,0,0,0.1) 30%,
            rgba(255,255,255,0.4) 65%,
            rgba(255,255,255,1) 85%,
            rgba(255,255,255,1) 100%
        )`
    },
    skipLink: {
        position: 'absolute',
        top: 12,
        right: 16,
        marginTop: 'var(--ion-safe-area-top)',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        zIndex: 2,
        padding: '8px 4px'
    },
    heroBottom: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 16
    },
    beaAvatar: {
        width: 96,
        height: 96,
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid rgba(255,255,255,0.6)'
    },
    greeting: {
        fontSize: 22,
        fontWeight: 500,
        color: '#032A37',
        marginTop: 12,
        textAlign: 'center'
    },
    cta: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 32px',
        textAlign: 'center',
        backgroundColor: '#fff'
    },
    heading: {
        fontSize: 18,
        fontWeight: 500,
        color: '#032A37',
        marginBottom: 12,
        lineHeight: 1.4
    },
    description: {
        fontSize: 14,
        color: '#687f87',
        lineHeight: 1.6,
        marginBottom: 32
    },
});

export const IntroScreen: FC<PropsWithChildren> = ({ children }) => {
    const classes = useStyles();
    const userState = useInstance(UserState);
    useSubscription(() => userState);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);
    const introScreenState = useInstance(IntroScreenState);
    useSubscription(() => introScreenState);

    const firstName = userState.state.firstName || '';
    const visible = introScreenState.isVisible();

    const { isFadingOut, fadeRef } = useFadeTransition({
        visible,
        durationMs: TRANSITION_MS
    });

    useEffect(() => {
        if (visible) {
            mixpanel.track('Intro Screen Viewed');
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRecord = () => {
        recordingState.triggerRecording({ initiatedFrom: 'Intro Screen', summaryPrompt: 'intro' });
        introScreenState.hide();
    };

    const handleSkip = () => {
        if (isFadingOut) {
            return;
        }
        mixpanel.track('Intro Skipped');
        introScreenState.hide();
    };

    // Not visible — render children only
    if (!visible && !isFadingOut) {
        return <>{children}</>;
    }

    // Showing intro (or fading out) — render children behind the overlay so the app is revealed during fade
    // Portal renders the overlay at document.body level so it sits above Ionic modals/sheets
    const overlay = (
        <div className={classes.overlay}>
            <div ref={fadeRef} className={classes.content}>
                <div className={classes.hero}>
                    <VideoBackground skipIncrement />
                    <div className={classes.gradientOverlay} />
                    <div className={classes.skipLink} onClick={handleSkip} data-id="intro-skip">
                        Skip
                    </div>
                    <div className={classes.heroBottom}>
                        <img
                            src="/assets/images/help/helper-bea-large.png"
                            alt="Bea"
                            className={classes.beaAvatar}
                        />
                        {firstName && (
                            <div className={classes.greeting}>
                                Nice to meet you, {firstName}.
                            </div>
                        )}
                    </div>
                </div>

                <div className={classes.cta}>
                    <div className={classes.heading}>
                        I&apos;d love to hear a little about what brought you here.
                    </div>
                    <div className={classes.description}>
                        What&apos;s going on, who you&apos;re looking after,
                        whatever&apos;s on your mind. I&apos;ll listen and remember it all.
                    </div>
                    <IntroRecordButton onRecord={handleRecord} disabled={isFadingOut} transitionMs={TRANSITION_MS} />
                </div>
            </div>
        </div>
    );

    return (
        <>
            {isFadingOut && children}
            {createPortal(overlay, document.body)}
        </>
    );
};
